import React, { useState, useEffect } from 'react';
import { ShoppingBag, Terminal, Power, Menu, X, Shield } from 'lucide-react';

// GlitchText component implementation
const GlitchText = ({ text, className }) => {
  return (
    <div className={`relative ${className}`}>
      <span className="relative z-10">{text}</span>
      <span className="absolute top-0 left-0.5 text-red-500 opacity-70 z-0 animate-pulse">{text}</span>
      <span className="absolute top-0 -left-0.5 text-blue-500 opacity-70 z-0 animate-pulse delay-75">{text}</span>
    </div>
  );
};

const Navbar = ({ currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch('https://api.4fingerstudios.com/api/auth/me', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setIsAuthenticated(true);
            setIsAdmin(userData.role === 'Admin');
          } else {
            setIsAuthenticated(false);
            setIsAdmin(false);
          }
        } catch (error) {
          setIsAuthenticated(false);
          setIsAdmin(false);
          console.error('Auth check failed:', error);
        }
      } else {
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    };
    checkAuth();
  }, []);

  const baseNavItems = [
    { icon: <Terminal size={20} />, href: '/', title: 'HOME' },
    { icon: <ShoppingBag size={20} />, href: '/Shop', title: 'Shop' },
    { icon: <Power size={20} />, href: isAuthenticated ? '/user-profile' : '/Profile', title: isAuthenticated ? 'PROFILE' : 'LOGIN' }
  ];

  const navItems = isAdmin
    ? [...baseNavItems.slice(0, -1), { icon: <Shield size={20} />, href: '/admin', title: 'ADMIN' }, baseNavItems[baseNavItems.length - 1]]
    : baseNavItems;

  return (
    <nav className="fixed top-0 w-full z-50 bg-black bg-opacity-80 shadow-md shadow-cyan-900/30 backdrop-blur-sm border-b border-cyan-900/30">
      <div className="max-w-6xl mx-auto">
        {/* Main navbar row */}
        <div className="flex items-center justify-between py-4 px-6">
          {/* Logo and current page indicator */}
          <div className="flex items-center space-x-3">
            <GlitchText 
              text="TMM" 
              className="text-3xl font-black hover:text-cyan-300 transition-colors duration-300" 
            />
            <div className="h-6 w-px bg-cyan-900/50"></div>
            <span className="text-sm font-medium text-cyan-500 uppercase tracking-wider">{currentPage || 'HOME'}</span>
          </div>

          {/* Mobile menu toggle button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-cyan-500 hover:text-cyan-300 transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Desktop navigation */}
          <div className="hidden md:block">
            <ul className="flex items-center space-x-10">
              {navItems.map((item, index) => (
                <li
                  key={index}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  className="group relative"
                >
                  <a 
                    href={item.href} 
                    className="flex flex-col items-center"
                    aria-label={item.title}
                  >
                    <div className={`p-2 transition-all duration-300 rounded-lg
                      ${index === activeIndex ? 'bg-cyan-900/30 text-cyan-300' : 'text-cyan-600 hover:text-cyan-400'}`}
                    >
                      {item.icon}
                    </div>
                    <span
                      className={`absolute -bottom-4 text-xs font-medium tracking-wide
                        transition-all duration-300 transform
                        ${index === activeIndex ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}
                      `}
                    >
                      {item.title}
                    </span>
                  </a>
                  {/* Animated underline indicator */}
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5
                    bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300
                    ${index === activeIndex ? 'w-full' : 'w-0'}`}
                  ></div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Mobile navigation menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden border-t border-cyan-900/30
          ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <ul className="flex flex-col p-4 space-y-3">
            {navItems.map((item, index) => (
              <li 
                key={index} 
                className="transform transition-all duration-300"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <a
                  href={item.href}
                  className="flex items-center gap-3 p-2 text-cyan-500 hover:text-cyan-300 rounded-lg hover:bg-cyan-900/20 transition-all duration-300"
                >
                  {item.icon}
                  <span>{item.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;